import { LogService } from '@ups/xplat/core';
import { sortByProperty } from '@ups/xplat/utils';
import {
  IDynamicCategory,
  IDynamicContainerMetadata,
  IDynamicTag,
  IDynamicTagDisplay,
} from '../models';
import { dynamicSupportedAppTags } from '../models/dto/display-dto-models';
import {
  dynamicConvertTagToName,
  dynamicGetAppTagName,
  dynamicGetAppTags,
  dynamicGetCategoryTags,
} from '../utils';
import {
  DynamicContainerListResponse,
  toDynamicContainerMetadataList,
} from './container-list-source';

// various debug logs which can be isolated
const logTag = 'DynamicRenderService';

// A type alias, not an interface: HttpClient's params need its implicit index signature.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type IDynamicContainerListParams = {
  tagNames?: string;
  $filter?: string;
  $format?: 'json';
};

export interface IDynamicContainerListRequest {
  usingAll: boolean;
  httpUrl: string;
  httpParams: IDynamicContainerListParams;
}

/** The feed DynamicRenderService.loadContainerList reads (WI 31925: split out of that method, unchanged). */
export function dynamicContainerListRequest(
  appTag: string,
  taggedWithAppOnly: boolean,
  showingAllVersions: boolean,
  publishedOnly: boolean
): IDynamicContainerListRequest {
  const usingAll = taggedWithAppOnly && !showingAllVersions;
  let httpUrl = usingAll
    ? `/api/DynamicContainer/all${publishedOnly ? '/published' : ''}`
    : `/api/DynamicContainer/odata`;
  const httpParams: IDynamicContainerListParams = {};
  if (usingAll) {
    // Note: issue with query param encoding
    // solution is to use params on the url instead
    // https://github.com/angular/angular/issues/18261#issuecomment-338352188
    // httpParams.tagNames = appTag;
    httpUrl = `${httpUrl}?tagNames=${appTag}`;
  } else {
    const params = [];
    if (taggedWithAppOnly) {
      const tagFilter = ` tag/tagName eq '${appTag}'`;
      params.push(`tags/any(tag:${tagFilter})`);
      if (!showingAllVersions) {
        params.push(`isActive eq true`);
      }
      httpParams.$filter = params.join(' and ');
    } else {
      // include all apps
      for (const dynamicAppTag of dynamicSupportedAppTags) {
        const tagFilter = ` tag/tagName eq '${dynamicAppTag.tag}'`;
        params.push(`tags/any(tag:${tagFilter})`);
      }
      httpParams.$filter = params.join(' or ');
    }
    httpParams.$format = 'json';
  }
  return { usingAll, httpUrl, httpParams };
}

/** The DynamicRenderService state a container-list load writes; the service passes itself. */
export interface IDynamicContainerListHost {
  dynamicContainerList: Array<IDynamicContainerMetadata>;
  dynamicContainerListAll: Array<IDynamicContainerMetadata>;
  dynamicAppTags: Array<IDynamicTagDisplay>;
  dynamicCategories: Array<IDynamicTagDisplay>;
  showingAllVersions: boolean;
  setupAppTags(tags: Array<IDynamicTag>): void;
  setupCategoryTags(
    tags: Array<IDynamicTag>,
    localCategories: Array<IDynamicCategory>
  ): void;
}

/**
 * Writes the published list, the full list (with every older version when showing all versions) and the app and
 * category tags from one container-list response onto `host`, in the order DynamicRenderService.loadContainerList did.
 */
export function applyDynamicContainerLists(
  host: IDynamicContainerListHost,
  list: DynamicContainerListResponse,
  localCategories: Array<IDynamicCategory>,
  log: LogService
) {
  log.debug(logTag, 'all dynamic container list:', list);
  host.dynamicContainerListAll = toDynamicContainerMetadataList(list);
  host.dynamicContainerList = [];
  host.dynamicAppTags = [];
  host.dynamicCategories = [];
  // handle published and category lists
  const olderVersions: Array<IDynamicContainerMetadata> = [];
  for (const c of host.dynamicContainerListAll) {
    const versionNumber = c.versionNumber ?? 0;
    c.dynamicContainerIdAndVersion = `${c.dynamicContainerId}:${versionNumber}`;
    if (c.isPublished && c.isActive) {
      host.dynamicContainerList.push(c);
    }
    if (host.showingAllVersions && versionNumber > 1) {
      log.debug(
        'adding older versions for:',
        c.dynamicContainerName,
        versionNumber
      );
      // add every version from the current down to 1
      for (let i = versionNumber - 1; i > 0; i--) {
        olderVersions.push({
          ...c,
          dynamicContainerIdAndVersion: `${c.dynamicContainerId}:${i}`,
          isPublished: false,
          versionNumber: i,
        });
      }
    }
    if (c.tags && c.tags.length) {
      host.setupAppTags(c.tags);
      host.setupCategoryTags(c.tags, localCategories);
    }
  }
  if (host.showingAllVersions && olderVersions.length) {
    log.debug(logTag, 'older versions:', olderVersions);
    host.dynamicContainerListAll =
      host.dynamicContainerListAll.concat(olderVersions);
    host.dynamicContainerListAll = host.dynamicContainerListAll.sort(
      sortByProperty('dynamicContainerName', true)
    );
  } else {
    host.dynamicContainerListAll = host.dynamicContainerListAll
      .sort(sortByProperty('dynamicContainerName', true))
      .sort(sortByProperty('isPublished'));
  }
}

/** Adds the supported app tags in `tags` that `appTags` does not hold yet. */
export function addDynamicAppTags(
  appTags: Array<IDynamicTagDisplay>,
  tags: Array<IDynamicTag>
) {
  // app tags can be open ended or prefixed with 'app:'
  // the app: prefixing began in October 2023
  // See notes inside this util method:
  for (const t of dynamicGetAppTags(tags)) {
    if (!appTags.find((cat) => cat.Id === t.tagId)) {
      const appName = dynamicGetAppTagName(t);
      // find the name defined locally for the matching app tag if available
      const appTag = dynamicSupportedAppTags.find((df) => df.tag === appName);
      if (appTag) {
        appTags.push({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Id: t.tagId,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Name: appTag.title,
          tagName: t.tagName || '',
          visible: [],
        });
      }
    }
  }
}

/** Adds the category tags in `tags` that `categories` does not hold yet, with the local category's icon and visibility. */
export function addDynamicCategoryTags(
  categories: Array<IDynamicTagDisplay>,
  tags: Array<IDynamicTag>,
  localCategories: Array<IDynamicCategory>
) {
  // category tags should always be prefixed with 'category:'
  const known = localCategories ?? [];
  for (const t of dynamicGetCategoryTags(tags)) {
    if (!categories.find((cat) => cat.Id === t.tagId)) {
      const categoryName = dynamicConvertTagToName(t);
      // find the icon defined locally for the matching category if available
      const defaultCategory = known.find((df) => df.title === categoryName);
      categories.push({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Id: t.tagId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Name: categoryName,
        tagName: t.tagName || '',
        icon: defaultCategory ? defaultCategory.icon : 'fa-file-contract',
        visible: defaultCategory ? defaultCategory.visible : [],
      });
    }
  }
}
